import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaTimes, FaChevronDown, FaCalendarAlt, FaArrowRight } from 'react-icons/fa';
import logo from '../../assets/images/logo.svg';
import theme from '../../theme';
import LanguageSelector from '../common/LanguageSelector';

const HeaderContainer = styled.header`
  background-color: ${theme.colors.white};
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0; left: 0; right: 0;
  z-index: 100;
  height: 75px;
  display: flex;
  align-items: center;
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Logo = styled(Link)`
  display: block;
  flex-shrink: 0;
  img { height: 50px; width: auto; }
`;

/* ─── HAMBURGER / CLOSE BUTTON ─── */
const MobileMenuBtn = styled.button`
  display: none;
  background: none; border: none; cursor: pointer;
  width: 40px; height: 40px; position: relative; z-index: 1002;
  @media (max-width: ${theme.breakpoints.tablet}) { display: flex; align-items: center; justify-content: center; }
`;

const HamburgerIcon = styled.div`
  width: 24px; height: 18px; position: relative;
  span {
    position: absolute; left: 0; width: 100%; height: 2.5px;
    background: ${theme.colors.secondary}; border-radius: 2px;
    transition: all 0.3s ease;
    &:nth-child(1) { top: 0; ${props => props.$open && css`top: 50%; transform: translateY(-50%) rotate(45deg);`} }
    &:nth-child(2) { top: 50%; transform: translateY(-50%); ${props => props.$open && css`opacity: 0;`} }
    &:nth-child(3) { bottom: 0; ${props => props.$open && css`bottom: 50%; transform: translateY(50%) rotate(-45deg);`} }
  }
`;

/* ─── DESKTOP MENU (unchanged) ─── */
const DesktopMenu = styled.div`
  display: flex; align-items: center; justify-content: flex-end;
  flex: 1; margin-left: 2rem;
  @media (max-width: ${theme.breakpoints.tablet}) { display: none; }
`;

const NavItem = styled.div`
  position: relative; margin: 0 0.6rem;
  white-space: nowrap;
`;

const NavLink = styled(Link)`
  color: ${theme.colors.secondary};
  font-family: ${theme.fonts.heading};
  font-weight: 500; padding: 1rem 0; display: block;
  font-size: 0.9rem;
  transition: ${theme.transition}; position: relative;
  &:hover { color: ${theme.colors.primary}; }
`;

const DesktopButton = styled(Link)`
  background-color: ${theme.colors.primary}; color: ${theme.colors.white};
  padding: 0.75rem 1.5rem; border-radius: ${theme.borderRadius};
  font-family: ${theme.fonts.heading}; font-weight: 500;
  transition: ${theme.transition}; margin-left: 1rem;
  &:hover { background-color: ${theme.colors.accent}; transform: translateY(-3px); box-shadow: 0 4px 8px rgba(0,0,0,0.1); }
`;

/* ─── DESKTOP MEGA MENU (unchanged) ─── */
const MegaMenuOverlay = styled.div`
  position: fixed; top: 75px; left: 0; right: 0; bottom: 0;
  background-color: rgba(0,0,0,0.5); z-index: 99;
  opacity: ${({isOpen}) => isOpen ? 1 : 0};
  visibility: ${({isOpen}) => isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  pointer-events: ${({isOpen}) => isOpen ? 'all' : 'none'};
  @media (max-width: ${theme.breakpoints.tablet}) { display: none; }
`;

const MegaMenuWrapper = styled.div`
  position: absolute; left: 0; right: 0; top: 75px; z-index: 100;
  background-color: white;
  border-top: 1px solid #eee; border-bottom: 1px solid #eee;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  display: ${({isOpen}) => isOpen ? 'block' : 'none'};
  @media (max-width: ${theme.breakpoints.tablet}) { display: none; }
`;

const MegaMenuContent = styled.div`
  max-width: 1200px; margin: 0 auto; width: 100%; padding: 0;
`;

const MegaMenu = styled.div`
  display: flex; width: 100%;
`;

const MenuCategoriesWrapper = styled.div`
  display: flex; background-color: ${theme.colors.primary};
  width: 250px; flex-shrink: 0;
`;

const MenuCategories = styled.div`width: 100%;`;

const MenuCategory = styled.div`
  padding: 1.2rem 1.5rem; color: white; font-weight: 500; cursor: pointer;
  background-color: ${props => props.active ? 'rgba(255,255,255,0.2)' : 'transparent'};
  text-transform: uppercase; font-size: 0.9rem; transition: all 0.2s ease;
  border-left: 5px solid ${props => props.active ? 'white' : 'transparent'};
  &:hover { background-color: rgba(255,255,255,0.2); border-left-color: white; }
`;

const MenuContentArea = styled.div`
  flex-grow: 1; background-color: white; padding: 2rem;
  display: ${props => props.active ? 'block' : 'none'};
`;

const MenuLinksGrid = styled.div`
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.5rem;
`;

const MenuItem = styled(Link)`
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; text-align: center;
  color: ${theme.colors.secondary}; padding: 1.25rem 1rem;
  transition: all 0.3s ease; background-color: white;
  border: 1px solid ${theme.colors.lightGray}; border-radius: 4px;
  height: 100%; min-height: 100px;
  box-shadow: 0 2px 5px rgba(0,0,0,0.05); text-decoration: none; font-weight: 500;
  &:hover { color: white; background-color: ${theme.colors.primary}; border-color: ${theme.colors.primary}; transform: translateY(-3px); box-shadow: 0 5px 15px rgba(0,0,0,0.1); }
`;

const FeaturedSection = styled.div`
  background-color: #f9f9f9; margin-top: 2rem; padding: 1.5rem;
  border-radius: ${theme.borderRadius}; border: 1px solid ${theme.colors.lightGray};
`;

const FeaturedTitle = styled.h3`font-size: 1.2rem; margin-bottom: 1rem; color: ${theme.colors.secondary};`;
const FeaturedContent = styled.p`font-size: 0.9rem; margin-bottom: 1.5rem; color: ${theme.colors.darkGray};`;
const FeaturedButton = styled(Link)`
  display: inline-block; padding: 0.5rem 1rem;
  background-color: ${theme.colors.primary}; color: white;
  border-radius: ${theme.borderRadius}; font-size: 0.9rem;
  transition: all 0.2s ease;
  &:hover { background-color: ${theme.colors.accent}; transform: translateY(-2px); }
`;

/* ─── MOBILE DRAWER ─── */
const slideIn = keyframes`from { transform: translateX(100%); } to { transform: translateX(0); }`;
const slideOut = keyframes`from { transform: translateX(0); } to { transform: translateX(100%); }`;

const MobileOverlay = styled.div`
  display: none;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: block; position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); z-index: 999;
    opacity: ${props => props.$open ? 1 : 0};
    visibility: ${props => props.$open ? 'visible' : 'hidden'};
    transition: opacity 0.3s ease, visibility 0.3s ease;
  }
`;

const MobileDrawer = styled.div`
  display: none;
  @media (max-width: ${theme.breakpoints.tablet}) {
    display: flex; flex-direction: column;
    position: fixed; top: 0; right: 0; bottom: 0;
    width: 85%; max-width: 380px;
    background: #fff; z-index: 1001;
    box-shadow: -4px 0 24px rgba(0,0,0,0.15);
    animation: ${props => props.$open ? slideIn : slideOut} 0.3s ease forwards;
    overflow: hidden;
  }
`;

const DrawerHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 1rem 1.25rem; border-bottom: 1px solid #f0f0f0;
  min-height: 64px;
`;

const DrawerLogo = styled.img`height: 36px;`;

const DrawerClose = styled.button`
  background: none; border: none; cursor: pointer;
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: ${theme.colors.secondary}; font-size: 1.1rem;
  transition: all 0.2s;
  &:hover { background: #f0f0f0; }
`;

const DrawerBody = styled.div`
  flex: 1; overflow-y: auto; padding: 0.5rem 0;
  -webkit-overflow-scrolling: touch;
`;

const DrawerSection = styled.div`
  border-bottom: 1px solid #f5f5f5;
`;

const DrawerNavItem = styled.button`
  display: flex; align-items: center; justify-content: space-between;
  width: 100%; padding: 0.95rem 1.25rem;
  background: none; border: none; cursor: pointer;
  font-family: ${theme.fonts.heading}; font-size: 0.95rem;
  font-weight: 600; color: ${theme.colors.secondary};
  transition: all 0.2s;
  &:hover { background: #fafafa; }
  svg {
    font-size: 0.7rem; color: #aaa;
    transition: transform 0.3s ease;
    ${props => props.$expanded && css`transform: rotate(180deg); color: ${theme.colors.primary};`}
  }
`;

const DrawerNavLink = styled(Link)`
  display: flex; align-items: center;
  width: 100%; padding: 0.95rem 1.25rem;
  font-family: ${theme.fonts.heading}; font-size: 0.95rem;
  font-weight: 600; color: ${theme.colors.secondary};
  text-decoration: none; transition: all 0.2s;
  &:hover { background: #fafafa; }
`;

const SubMenu = styled.div`
  max-height: ${props => props.$open ? '600px' : '0'};
  overflow: hidden; transition: max-height 0.35s ease;
  background: #fafafa;
`;

const SubCategory = styled.div`
  padding: 0.5rem 1.25rem 0.25rem;
  font-size: 0.7rem; font-weight: 700; letter-spacing: 1.5px;
  color: ${theme.colors.primary}; text-transform: uppercase;
  margin-top: 0.25rem;
`;

const SubLink = styled(Link)`
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.7rem 1.25rem 0.7rem 1.5rem;
  font-size: 0.88rem; font-weight: 500;
  color: ${theme.colors.secondary}; text-decoration: none;
  transition: all 0.15s;
  &:hover { background: #f0f0f0; color: ${theme.colors.primary}; }
  &:active { background: #e8e8e8; }
  svg { font-size: 0.6rem; color: #ccc; }
`;

const DrawerFooter = styled.div`
  padding: 1rem 1.25rem; border-top: 1px solid #f0f0f0;
  background: #fff;
`;

const DrawerCTA = styled(Link)`
  display: flex; align-items: center; justify-content: center; gap: 0.6rem;
  width: 100%; padding: 0.9rem; border-radius: 12px;
  background: ${theme.colors.primary}; color: white;
  font-family: ${theme.fonts.heading}; font-size: 0.95rem;
  font-weight: 700; text-decoration: none;
  transition: all 0.2s; margin-bottom: 0.75rem;
  &:hover { background: #c0392b; }
  &:active { transform: scale(0.98); }
  svg { font-size: 0.85rem; }
`;

const DrawerLangRow = styled.div`
  display: flex; align-items: center; justify-content: center;
`;

const Header = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const menuTimeoutRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    if (openDropdown || isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [openDropdown, isMenuOpen]);

  useEffect(() => {
    function handleClickOutside(event) {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = (dropdown) => {
    if (menuTimeoutRef.current) { clearTimeout(menuTimeoutRef.current); menuTimeoutRef.current = null; }
    if (window.innerWidth > 768) {
      const item = menuItems.find(i => i.titleKey === dropdown);
      if (!item || !item.categories) { setOpenDropdown(null); return; }
      setOpenDropdown(dropdown);
      if (item.categories.length > 0) setActiveCategory(item.categories[0].id);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      menuTimeoutRef.current = setTimeout(() => { setOpenDropdown(null); setActiveCategory(null); }, 300);
    }
  };

  const toggleMobile = () => {
    setIsMenuOpen(!isMenuOpen);
    setMobileExpanded(null);
  };

  const closeMobile = () => {
    setIsMenuOpen(false);
    setMobileExpanded(null);
  };

  const toggleMobileSection = (key) => {
    setMobileExpanded(mobileExpanded === key ? null : key);
  };

  const menuItems = [
    {
      titleKey: 'nav.corporate',
      categories: [
        { id: 'hakkimizda', nameKey: 'nav.menu.corporate.about.title',
          links: [
            { titleKey: 'nav.menu.corporate.about.companyProfile', path: `/${lang}/about` },
            { titleKey: 'nav.menu.corporate.about.missionVision', path: `/${lang}/mission-vision` },
          ],
          featured: { titleKey: 'nav.menu.corporate.about.featured.title', contentKey: 'nav.menu.corporate.about.featured.description', buttonTextKey: 'nav.menu.corporate.about.featured.button', buttonLink: `/${lang}/about` }
        },
        { id: 'medya', nameKey: 'nav.menu.corporate.media.title',
          links: [{ titleKey: 'nav.menu.corporate.media.news', path: `/${lang}/news` }],
          featured: { titleKey: 'nav.menu.corporate.media.featured.title', contentKey: 'nav.menu.corporate.media.featured.description', buttonTextKey: 'nav.menu.corporate.media.featured.button', buttonLink: `/${lang}/news` }
        },
        { id: 'kurumsal-politikalar', nameKey: 'nav.menu.corporate.policies.title',
          links: [
            { titleKey: 'nav.menu.corporate.policies.privacy', path: `/${lang}/privacy-policy` },
            { titleKey: 'nav.menu.corporate.policies.cookies', path: `/${lang}/cookies` },
            { titleKey: 'nav.menu.corporate.policies.kvkk', path: `/${lang}/gdpr` },
          ],
          featured: { titleKey: 'nav.menu.corporate.policies.featured.title', contentKey: 'nav.menu.corporate.policies.featured.description', buttonTextKey: 'nav.menu.corporate.policies.featured.button', buttonLink: `/${lang}/privacy-policy` }
        }
      ]
    },
    {
      titleKey: 'nav.solutions',
      categories: [
        { id: 'b2b-platformu', nameKey: 'nav.menu.solutions.platform.title',
          links: [
            { titleKey: 'nav.menu.solutions.platform.ecommerce', path: `/${lang}/ecommerce` },
            { titleKey: 'nav.menu.solutions.platform.payment', path: `/${lang}/payment` },
            { titleKey: 'nav.menu.solutions.platform.dealer', path: `/${lang}/dealer-management` },
          ],
          featured: { titleKey: 'nav.menu.solutions.platform.featured.title', contentKey: 'nav.menu.solutions.platform.featured.description', buttonTextKey: 'nav.menu.solutions.platform.featured.button', buttonLink: `/${lang}/ecommerce` }
        },
        { id: 'sektorel-cozumler', nameKey: 'nav.menu.solutions.industries.title',
          links: [
            { titleKey: 'industries.items.food', path: `/${lang}/industries/food` },
            { titleKey: 'industries.items.automotive', path: `/${lang}/industries/automotive` },
            { titleKey: 'industries.items.tire', path: `/${lang}/industries/tire` },
            { titleKey: 'industries.items.textile', path: `/${lang}/industries/textile` },
            { titleKey: 'industries.items.construction', path: `/${lang}/industries/construction` },
            { titleKey: 'industries.items.home', path: `/${lang}/industries/home` },
            { titleKey: 'industries.items.electronics', path: `/${lang}/industries/electronics` },
            { titleKey: 'industries.items.publishing', path: `/${lang}/industries/publishing` },
            { titleKey: 'industries.items.optical', path: `/${lang}/industries/optical` },
          ],
          featured: { titleKey: 'nav.menu.solutions.industries.featured.title', contentKey: 'nav.menu.solutions.industries.featured.description', buttonTextKey: 'nav.menu.solutions.industries.featured.button', buttonLink: `/${lang}/industries` }
        },
        { id: 'ozellikler-faydalar', nameKey: 'nav.menu.solutions.features.title',
          links: [
            { titleKey: 'nav.menu.solutions.features.features', path: `/${lang}/features` },
            { titleKey: 'nav.menu.solutions.features.benefits', path: `/${lang}/benefits` },
          ],
          featured: { titleKey: 'nav.menu.solutions.features.featured.title', contentKey: 'nav.menu.solutions.features.featured.description', buttonTextKey: 'nav.menu.solutions.features.featured.button', buttonLink: `/${lang}/benefits` }
        }
      ]
    },
    { titleKey: 'nav.integration', path: `/${lang}/integration` },
    {
      titleKey: 'nav.pricing',
      categories: [
        { id: 'fiyatlandirma', nameKey: 'nav.menu.pricing.title',
          links: [
            { titleKey: 'nav.menu.pricing.packages', path: `/${lang}/pricing` },
            { titleKey: 'nav.menu.pricing.custom', path: `/${lang}/custom-prices` },
          ],
          featured: { titleKey: 'nav.menu.pricing.featured.title', contentKey: 'nav.menu.pricing.featured.description', buttonTextKey: 'nav.menu.pricing.featured.button', buttonLink: `/${lang}/pricing` }
        }
      ]
    },
    { titleKey: 'nav.references', path: `/${lang}/references` },
    {
      titleKey: 'nav.contact',
      categories: [
        { id: 'iletisim-bilgileri', nameKey: 'nav.menu.contact.title',
          links: [
            { titleKey: 'nav.menu.contact.contact', path: `/${lang}/contact` },
            { titleKey: 'nav.menu.contact.demo', path: `/${lang}/demo` },
            { titleKey: 'nav.menu.contact.faq', path: `/${lang}/faq` },
          ],
          featured: { titleKey: 'nav.menu.contact.featured.title', contentKey: 'nav.menu.contact.featured.description', buttonTextKey: 'nav.menu.contact.featured.button', buttonLink: `/${lang}/contact` }
        }
      ]
    }
  ];

  return (
    <>
      <HeaderContainer ref={headerRef}>
        <div className="container">
          <Nav>
            <Logo to={`/${lang || 'en'}`}>
              <img src={logo} alt="PetekB2B Logo" />
            </Logo>

            {/* ─── DESKTOP MENU ─── */}
            <DesktopMenu>
              {menuItems.map((item, index) => (
                <NavItem key={index} onMouseEnter={() => handleMouseEnter(item.titleKey)} onMouseLeave={handleMouseLeave}>
                  <NavLink to={item.path || '#'}>{t(item.titleKey)}</NavLink>
                </NavItem>
              ))}
              <DesktopButton to={`/${lang}/demo`}>{t('nav.demo')}</DesktopButton>
              <LanguageSelector />
            </DesktopMenu>

            {/* ─── MOBILE HAMBURGER ─── */}
            <MobileMenuBtn onClick={toggleMobile} aria-label="Menu">
              <HamburgerIcon $open={isMenuOpen}>
                <span /><span /><span />
              </HamburgerIcon>
            </MobileMenuBtn>
          </Nav>
        </div>

        {/* ─── DESKTOP MEGA MENU ─── */}
        <MegaMenuOverlay isOpen={openDropdown !== null} onClick={() => setOpenDropdown(null)} />
        <MegaMenuWrapper
          isOpen={openDropdown !== null}
          onMouseEnter={() => { if (menuTimeoutRef.current) { clearTimeout(menuTimeoutRef.current); menuTimeoutRef.current = null; } }}
          onMouseLeave={handleMouseLeave}
        >
          <MegaMenuContent>
            {menuItems.map((item, index) => {
              if (openDropdown !== item.titleKey || !item.categories) return null;
              return (
                <MegaMenu key={index}>
                  <MenuCategoriesWrapper>
                    <MenuCategories>
                      {item.categories.map((cat, ci) => (
                        <MenuCategory key={ci} active={activeCategory === cat.id} onClick={() => setActiveCategory(cat.id)}>
                          {t(cat.nameKey)}
                        </MenuCategory>
                      ))}
                    </MenuCategories>
                  </MenuCategoriesWrapper>
                  {item.categories.map((cat, ci) => (
                    <MenuContentArea key={ci} active={activeCategory === cat.id}>
                      <MenuLinksGrid>
                        {cat.links.map((link, li) => (
                          <MenuItem key={li} to={link.path} onClick={() => setOpenDropdown(null)}>{t(link.titleKey)}</MenuItem>
                        ))}
                      </MenuLinksGrid>
                      {cat.featured && (
                        <FeaturedSection>
                          <FeaturedTitle>{t(cat.featured.titleKey)}</FeaturedTitle>
                          <FeaturedContent>{t(cat.featured.contentKey)}</FeaturedContent>
                          <FeaturedButton to={cat.featured.buttonLink} onClick={() => setOpenDropdown(null)}>
                            {t(cat.featured.buttonTextKey)}
                          </FeaturedButton>
                        </FeaturedSection>
                      )}
                    </MenuContentArea>
                  ))}
                </MegaMenu>
              );
            })}
          </MegaMenuContent>
        </MegaMenuWrapper>
      </HeaderContainer>

      {/* ─── MOBILE DRAWER ─── */}
      <MobileOverlay $open={isMenuOpen} onClick={closeMobile} />
      {isMenuOpen && (
        <MobileDrawer $open={isMenuOpen}>
          <DrawerHeader>
            <DrawerLogo src={logo} alt="PetekB2B" />
            <DrawerClose onClick={closeMobile}><FaTimes /></DrawerClose>
          </DrawerHeader>

          <DrawerBody>
            {menuItems.map((item, index) => {
              if (!item.categories) {
                return (
                  <DrawerSection key={index}>
                    <DrawerNavLink to={item.path} onClick={closeMobile}>
                      {t(item.titleKey)}
                    </DrawerNavLink>
                  </DrawerSection>
                );
              }
              return (
                <DrawerSection key={index}>
                  <DrawerNavItem
                    $expanded={mobileExpanded === item.titleKey}
                    onClick={() => toggleMobileSection(item.titleKey)}
                  >
                    {t(item.titleKey)}
                    <FaChevronDown />
                  </DrawerNavItem>
                  <SubMenu $open={mobileExpanded === item.titleKey}>
                    {item.categories.map((cat, ci) => (
                      <React.Fragment key={ci}>
                        {item.categories.length > 1 && (
                          <SubCategory>{t(cat.nameKey)}</SubCategory>
                        )}
                        {cat.links.map((link, li) => (
                          <SubLink key={li} to={link.path} onClick={closeMobile}>
                            <FaArrowRight /> {t(link.titleKey)}
                          </SubLink>
                        ))}
                      </React.Fragment>
                    ))}
                  </SubMenu>
                </DrawerSection>
              );
            })}
          </DrawerBody>

          <DrawerFooter>
            <DrawerCTA to={`/${lang}/demo`} onClick={closeMobile}>
              <FaCalendarAlt /> {t('nav.demo')}
            </DrawerCTA>
            <DrawerLangRow>
              <LanguageSelector />
            </DrawerLangRow>
          </DrawerFooter>
        </MobileDrawer>
      )}
    </>
  );
};

export default Header;
